/**
 * Hook TanStack Query — journal des publications d'une œuvre (S6 PR4).
 *
 * `GET /api/works/{work_id}/publications` — toutes plateformes confondues,
 * shape `PublicationJournalEntry` (avec mockups thumbnails presigned +
 * agrégats sales/metrics).
 *
 * À distinguer de `useWorkPublications` (déjà existant) qui consomme
 * `GET /api/works/{work_id}/publications/{platform}` filtré par
 * plateforme — ce dernier alimente `<PlatformPublishCard>` (par
 * plateforme), tandis que celui-ci alimente `<WorkPublicationsTab>`
 * (toutes plateformes).
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/utils/api/client'
import type { components } from '@/types/api-generated'

export type PublicationJournalEntry = components['schemas']['PublicationJournalEntry']

export const workPublicationsJournalKeys = {
  all: ['work-publications-journal'] as const,
  forWork: (workId: number, includeAllMockups: boolean) =>
    [...workPublicationsJournalKeys.all, 'work', workId, includeAllMockups] as const,
}

interface UseJournalArgs {
  workId: number | null | undefined
  includeAllMockups?: boolean
}

export function useWorkPublicationsJournal({ workId, includeAllMockups = false }: UseJournalArgs) {
  return useQuery<PublicationJournalEntry[]>({
    queryKey: workPublicationsJournalKeys.forWork(workId ?? -1, includeAllMockups),
    queryFn: async () => {
      const { data } = await apiClient.get<PublicationJournalEntry[]>(
        `/works/${workId}/publications`,
        { params: { include_all_mockups: includeAllMockups } },
      )
      return data
    },
    enabled: workId !== null && workId !== undefined && workId > 0,
  })
}
